import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { BulkUploadTypes, UploadTypeConfig, uploadTypeConfigMap } from 'constants/bulkUpload';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useReduxSelector from 'utils/useReduxSelector';
import { useHistory } from 'react-router-dom';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { listCustomFieldDefinitions } from 'api/gql-utils/employeeDataSettings/queries/listCustomFieldDefinitions';
import { CACHE_TTL } from 'utils/cache';

export const BULK_UPLOAD_CUSTOM_FIELD_DEFINITIONS = 'BULK_UPLOAD_CUSTOM_FIELD_DEFINITIONS';

// Only the flows that carry HRIS custom field columns need definitions.
const CUSTOM_FIELD_UPLOAD_TYPES: BulkUploadTypes[] = [
  BulkUploadTypes.TYPE_BULK_ONBOARDING,
  BulkUploadTypes.TYPE_BULK_EMPLOYEE_UPDATE,
];

export const useBulkUploadTypeConfig = (
  bulkUploadType: BulkUploadTypes,
  stagingRecordData?: BulkUploadStagingDetailsSchemaContract[1] | null,
): UploadTypeConfig => {
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const featureFlags = useReduxSelector(loggedInUserSelectors.features);
  const history = useHistory();

  // Custom field definitions give the preview one column per field with the
  // configured label; while loading (or if the fetch fails) configs fall back
  // to the single combined "Custom Fields" column.
  // staleTime is essential here: the upload/preview pages poll their status
  // every second, and each poll re-render would otherwise refetch this query
  // (default staleTime 0), producing a request loop and visible flicker.
  const definitionsQuery = useQuery({
    queryKey: [BULK_UPLOAD_CUSTOM_FIELD_DEFINITIONS],
    queryFn: () => listCustomFieldDefinitions({}),
    enabled: Boolean(featureFlags?.hrisCustomFields) && CUSTOM_FIELD_UPLOAD_TYPES.includes(bulkUploadType),
    staleTime: CACHE_TTL.MEDIUM,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const customFieldDefinitions = useMemo(
    () =>
      (definitionsQuery.data?.payrollCustomFieldDefinitions?.definitions ?? [])
        .slice()
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [definitionsQuery.data],
  );

  const uploadTypeConfig = uploadTypeConfigMap[bulkUploadType]?.({
    isRxCurrentAccountMerchant,
    featureFlags,
    history,
    stagingRecordData: stagingRecordData || undefined,
    customFieldDefinitions,
  });

  return uploadTypeConfig;
};
